import { ConfirmDialog, Dialog, NameDialog } from "./Dialog.tsx";
import { parentOf, type TreeSelection } from "./FileTree.tsx";

export type ExplorerAction = "newFile" | "newFolder" | "upload" | "rename" | "delete";

/** Folder in which "New …" creates entries: the selected folder, or the selected file's folder. */
export const baseDir = (t: TreeSelection | null) =>
  !t ? "" : t.kind === "directory" ? t.path : parentOf(t.path);

export function ExplorerContextMenu({
  x,
  y,
  kind,
  isRoot = false,
  onAction,
}: {
  x: number;
  y: number;
  kind: TreeSelection["kind"];
  /** Project root: only create/upload actions (it cannot be renamed or deleted). */
  isRoot?: boolean;
  onAction: (a: ExplorerAction) => void;
}) {
  const isFile = kind === "file";
  return (
    <ul className="context-menu" style={{ left: x, top: y }} role="menu" data-testid="fs-context-menu">
      {!isFile && (
        <>
          <li>
            <button onClick={() => onAction("newFile")}>New File</button>
          </li>
          <li>
            <button onClick={() => onAction("newFolder")}>New Folder</button>
          </li>
          <li>
            <button onClick={() => onAction("upload")}>Upload Files…</button>
          </li>
        </>
      )}
      {!isRoot && (
        <>
          <li>
            <button onClick={() => onAction("rename")}>Rename…</button>
          </li>
          <li>
            <button className="danger-text" onClick={() => onAction("delete")}>
              Delete…
            </button>
          </li>
        </>
      )}
    </ul>
  );
}

export function CollisionDialog({
  fileName,
  targetDir,
  projectName,
  onResolve,
}: {
  fileName: string;
  targetDir: string;
  projectName: string;
  onResolve: (action: "overwrite" | "rename" | "cancel") => void;
}) {
  const locationName = targetDir || `${projectName} (root)`;
  return (
    <Dialog title="File Already Exists" onClose={() => onResolve("cancel")}>
      <p>
        A file named <code>{fileName}</code> already exists in <code>{locationName}</code>.
      </p>
      <div className="row end gap-2" style={{ marginTop: 16 }}>
        <button type="button" onClick={() => onResolve("cancel")}>
          Cancel
        </button>
        <button type="button" onClick={() => onResolve("rename")}>
          Keep Both
        </button>
        <button type="button" className="primary" onClick={() => onResolve("overwrite")}>
          Replace Existing
        </button>
      </div>
    </Dialog>
  );
}

interface DialogProps {
  action: ExplorerAction;
  target: TreeSelection | null;
  projectName: string;
  targetHasDirtyTabs: boolean;
  onClose: () => void;
  onCreate: (kind: "file" | "folder", dir: string, name: string) => Promise<void>;
  onRename: (target: TreeSelection, newPath: string) => Promise<void>;
  onDelete: (target: TreeSelection) => Promise<void>;
}

export function ExplorerDialog(p: DialogProps) {
  const { action, target } = p;
  if (action === "newFile" || action === "newFolder") {
    const dir = baseDir(target);
    return (
      <NameDialog
        title={action === "newFile" ? "New File" : "New Folder"}
        initial=""
        hint={`In ${dir || `${p.projectName} (root)`}. Use "/" to create nested folders.`}
        confirmLabel="Create"
        onClose={p.onClose}
        onSubmit={(name) => p.onCreate(action === "newFile" ? "file" : "folder", dir, name)}
      />
    );
  }
  if (!target) return null;
  if (action === "rename") {
    return (
      <NameDialog
        title={`Rename ${target.kind === "directory" ? "Folder" : "File"}`}
        initial={target.path}
        hint="Edit the path to move the entry within the Project."
        confirmLabel="Rename"
        onClose={p.onClose}
        onSubmit={(newPath) => p.onRename(target, newPath)}
      />
    );
  }
  const noun = target.kind === "directory" ? "folder" : target.kind === "symlink" ? "link" : "file";
  return (
    <ConfirmDialog
      title={`Delete ${noun}?`}
      danger
      confirmLabel="Delete"
      message={
        <p>
          Permanently delete <code>{target.path}</code>
          {target.kind === "directory" ? " and everything inside it" : ""}? This cannot be undone.
          {target.kind === "symlink" && " Only the link is removed, not its target."}
          {p.targetHasDirtyTabs && <strong> Unsaved changes in open tabs will be lost.</strong>}
        </p>
      }
      onClose={p.onClose}
      onConfirm={() => p.onDelete(target)}
    />
  );
}
